import React, { useEffect, useMemo, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CustomerLite } from '../Plants/CreatePlant';
import { AssetDetailsResponse } from '../../Services/serviceURL';
import { filterSearchableList } from './assetSummaryDetailsHelpers';

type AssetDetails = AssetDetailsResponse;

const DropdownArrow = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5"
      stroke="#FCFCFD"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type DropdownOption = {
  key: string;
  value: string;
  label: string;
};

type SearchableDropdownProps = {
  minWidth: string;
  isOpen: boolean;
  search: string;
  selectedId: string;
  selectedLabel: string;
  searchPlaceholder: string;
  emptyPlaceholder: string;
  allOptionLabel: string;
  options: DropdownOption[];
  onToggle: () => void;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onSelectOption: (value: string) => void;
};

const searchableInputValue = (
  isOpen: boolean,
  search: string,
  selectedId: string,
  selectedLabel: string
): string => (isOpen ? search : selectedId ? selectedLabel : '');

const searchableInputPlaceholder = (
  isOpen: boolean,
  searchPlaceholder: string,
  selectedId: string,
  selectedLabel: string,
  emptyPlaceholder: string
): string => (isOpen ? searchPlaceholder : selectedId ? selectedLabel : emptyPlaceholder);

const openDropdownOnArrowKey = (e: React.KeyboardEvent, open: () => void) => {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') open();
};

const AssetSummarySearchableDropdown: React.FC<SearchableDropdownProps> = ({
  minWidth,
  isOpen,
  search,
  selectedId,
  selectedLabel,
  searchPlaceholder,
  emptyPlaceholder,
  allOptionLabel,
  options,
  onToggle,
  onSearchChange,
  onSelectAll,
  onSelectOption,
}) => (
  <div className="searchable-select-container" style={{ display: 'inline-block', minWidth }}>
    <div className="searchable-select-input" onClick={onToggle}>
      <input
        type="text"
        value={searchableInputValue(isOpen, search, selectedId, selectedLabel)}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => openDropdownOnArrowKey(e, onToggle)}
        placeholder={searchableInputPlaceholder(
          isOpen,
          searchPlaceholder,
          selectedId,
          selectedLabel,
          emptyPlaceholder
        )}
        className="asset-summary-select searchable-input"
        readOnly={!isOpen}
      />
      <span className={`dropdown-arrow assetsumary-arrow ${isOpen ? 'open' : ''}`}>
        <DropdownArrow />
      </span>
    </div>
    {isOpen && (
      <div className="searchable-dropdown">
        <div
          className={`dropdown-option ${selectedId === '' ? 'selected' : ''}`}
          onClick={onSelectAll}
        >
          {allOptionLabel}
        </div>
        {options.map((option) => (
          <div
            key={option.key}
            className={`dropdown-option ${option.value === selectedId ? 'selected' : ''}`}
            onClick={() => onSelectOption(option.value)}
          >
            {option.label}
          </div>
        ))}
      </div>
    )}
  </div>
);

type Props = {
  customers: CustomerLite[];
  sites: { siteId: string; site: string }[];
  plants: { plantId: string; plant: string }[];
  assets: { id: string; assetId: string; assetName: string }[];
  selectedCustomerId: string;
  selectedSiteId: string;
  selectedPlantId: string;
  selectedAssetId: string;
  handleCustomerChange: (id: string) => void;
  setSelectedSiteId: (id: string) => void;
  setSelectedPlantId: (id: string) => void;
  setSelectedAssetId: (id: string) => void;
  setSelectedAssetRecordId: (id: string) => void;
  setAssetDetails: (details: AssetDetails | null) => void;
  setPlants: (plants: { plantId: string; plant: string }[]) => void;
  setAssets: (assets: { id: string; assetId: string; assetName: string }[]) => void;
  refreshPlantsForSite: (siteId: string) => Promise<void>;
  refreshAssetsForPlant: (plantId: string) => Promise<void>;
  navigate: NavigateFunction;
};

type DropdownKind = 'customer' | 'site' | 'plant' | 'asset';

const useFilterDropdownOpenState = () => {
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [isSiteOpen, setIsSiteOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [isPlantOpen, setIsPlantOpen] = useState(false);
  const [plantSearch, setPlantSearch] = useState('');
  const [isAssetOpen, setIsAssetOpen] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');

  const closeOtherDropdowns = (except?: DropdownKind) => {
    if (except !== 'customer') setIsCustomerOpen(false);
    if (except !== 'site') setIsSiteOpen(false);
    if (except !== 'plant') setIsPlantOpen(false);
    if (except !== 'asset') setIsAssetOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest('.searchable-select-container')) {
        closeOtherDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (kind: DropdownKind, isOpen: boolean, setOpen: (open: boolean) => void) => {
    setOpen(!isOpen);
    closeOtherDropdowns(kind);
  };

  return {
    isCustomerOpen,
    setIsCustomerOpen,
    customerSearch,
    setCustomerSearch,
    isSiteOpen,
    setIsSiteOpen,
    siteSearch,
    setSiteSearch,
    isPlantOpen,
    setIsPlantOpen,
    plantSearch,
    setPlantSearch,
    isAssetOpen,
    setIsAssetOpen,
    assetSearch,
    setAssetSearch,
    toggleDropdown,
  };
};

const useAssetSummaryFilterSelections = (props: Props, dropdownState: ReturnType<typeof useFilterDropdownOpenState>) => {
  const {
    customers,
    sites,
    plants,
    assets,
    selectedCustomerId,
    selectedSiteId,
    selectedPlantId,
    selectedAssetId,
    handleCustomerChange,
    setSelectedSiteId,
    setSelectedPlantId,
    setSelectedAssetId,
    setSelectedAssetRecordId,
    setAssetDetails,
    setPlants,
    setAssets,
    refreshPlantsForSite,
    refreshAssetsForPlant,
    navigate,
  } = props;

  const {
    customerSearch,
    setCustomerSearch,
    setIsCustomerOpen,
    siteSearch,
    setSiteSearch,
    setIsSiteOpen,
    plantSearch,
    setPlantSearch,
    setIsPlantOpen,
    assetSearch,
    setAssetSearch,
    setIsAssetOpen,
  } = dropdownState;

  const filteredCustomersList = useMemo(
    () => filterSearchableList(customers, customerSearch, (c) => [c.customerName, c.soldTo]),
    [customers, customerSearch]
  );
  const filteredSitesList = useMemo(
    () => filterSearchableList(sites, siteSearch, (s) => [s.site, s.siteId]),
    [sites, siteSearch]
  );
  const filteredPlantsList = useMemo(
    () => filterSearchableList(plants, plantSearch, (p) => [p.plant, p.plantId]),
    [plants, plantSearch]
  );
  const filteredAssetsList = useMemo(
    () => filterSearchableList(assets, assetSearch, (a) => [a.assetName, a.assetId]),
    [assets, assetSearch]
  );

  const selectedCustomerLabel = customers.find((c) => c.customerId === selectedCustomerId)?.customerName || '';
  const selectedSiteLabel = sites.find((s) => s.siteId === selectedSiteId)?.site || '';
  const selectedPlantLabel = plants.find((p) => p.plantId === selectedPlantId)?.plant || '';
  const selectedAssetLabel = assets.find((a) => a.assetId === selectedAssetId)?.assetName || '';

  const customerOptions = useMemo(
    () =>
      filteredCustomersList.map((customer, index) => ({
        key: customer.id || `${customer.customerId}-${index}`,
        value: customer.customerId,
        label: `${customer.customerName}${customer.soldTo ? ` (${customer.soldTo})` : ''}`,
      })),
    [filteredCustomersList]
  );

  const siteOptions = useMemo(
    () =>
      filteredSitesList.map((site, index) => ({
        key: `${site.siteId}-${index}`,
        value: site.siteId,
        label: site.site,
      })),
    [filteredSitesList]
  );

  const plantOptions = useMemo(
    () =>
      filteredPlantsList.map((plant, index) => ({
        key: `${plant.plantId}-${index}`,
        value: plant.plantId,
        label: plant.plant,
      })),
    [filteredPlantsList]
  );

  const assetOptions = useMemo(
    () =>
      filteredAssetsList.map((asset, index) => ({
        key: `${asset.id}-${index}`,
        value: asset.assetId,
        label: asset.assetName,
      })),
    [filteredAssetsList]
  );

  const clearSiteDependents = () => {
    setSelectedPlantId('');
    setSelectedAssetId('');
    setSelectedAssetRecordId('');
    setAssetDetails(null);
    setPlants([]);
    setAssets([]);
  };

  const closeCustomerDropdown = () => {
    setIsCustomerOpen(false);
    setCustomerSearch('');
  };

  const closeSiteDropdown = () => {
    setIsSiteOpen(false);
    setSiteSearch('');
  };

  const closePlantDropdown = () => {
    setIsPlantOpen(false);
    setPlantSearch('');
  };

  const closeAssetDropdown = () => {
    setIsAssetOpen(false);
    setAssetSearch('');
  };

  const handleSiteSelect = async (newSiteId: string) => {
    if (selectedSiteId === newSiteId) {
      setPlants([]);
      setAssets([]);
      setSelectedPlantId('');
      setSelectedAssetId('');
      setSelectedAssetRecordId('');
      setAssetDetails(null);
      setPlantSearch('');
      setAssetSearch('');
      await refreshPlantsForSite(newSiteId);
      setIsSiteOpen(false);
      return;
    }
    setSelectedSiteId(newSiteId);
    clearSiteDependents();
    closeSiteDropdown();
  };

  const handlePlantSelect = async (newPlantId: string) => {
    if (selectedPlantId === newPlantId) {
      setSelectedAssetId('');
      setSelectedAssetRecordId('');
      setAssetDetails(null);
      setAssetSearch('');
      await refreshAssetsForPlant(newPlantId);
      setSelectedPlantId(newPlantId);
    } else {
      setSelectedPlantId(newPlantId);
      setSelectedAssetId('');
      setSelectedAssetRecordId('');
      setAssetDetails(null);
      await refreshAssetsForPlant(newPlantId);
    }
    closePlantDropdown();
  };

  const handleAssetSelect = (newAssetId: string) => {
    setSelectedAssetId(newAssetId);
    const selectedAsset = assets.find((a) => a.assetId === newAssetId);
    if (!selectedAsset) {
      setSelectedAssetRecordId('');
      closeAssetDropdown();
      return;
    }

    setSelectedAssetRecordId(selectedAsset.id);
    const params = new URLSearchParams();
    if (selectedCustomerId) params.append('customerId', selectedCustomerId);
    if (selectedSiteId) params.append('siteId', selectedSiteId);
    if (selectedPlantId) params.append('plantId', selectedPlantId);
    params.append('selectedAssetId', newAssetId);

    const queryString = params.toString();
    const url = queryString ? `/asset-details/${newAssetId}?${queryString}` : `/asset-details/${newAssetId}`;
    navigate(url, {
      replace: true,
      state: {
        assetName: selectedAsset.assetName,
        breadcrumbTitle: selectedAsset.assetName,
      },
    });
    closeAssetDropdown();
  };

  return {
    selectedCustomerLabel,
    selectedSiteLabel,
    selectedPlantLabel,
    selectedAssetLabel,
    customerOptions,
    siteOptions,
    plantOptions,
    assetOptions,
    handleCustomerChange,
    clearSiteDependents,
    closeCustomerDropdown,
    closeSiteDropdown,
    closePlantDropdown,
    closeAssetDropdown,
    handleSiteSelect,
    handlePlantSelect,
    handleAssetSelect,
    setSelectedSiteId,
    setSelectedPlantId,
    setSelectedAssetId,
    setSelectedAssetRecordId,
    setAssetDetails,
    setAssets,
  };
};

const AssetSummaryFilterDropdowns: React.FC<Props> = (props) => {
  const dropdownState = useFilterDropdownOpenState();
  const selections = useAssetSummaryFilterSelections(props, dropdownState);

  const {
    isCustomerOpen,
    setIsCustomerOpen,
    customerSearch,
    setCustomerSearch,
    isSiteOpen,
    setIsSiteOpen,
    siteSearch,
    setSiteSearch,
    isPlantOpen,
    setIsPlantOpen,
    plantSearch,
    setPlantSearch,
    isAssetOpen,
    setIsAssetOpen,
    assetSearch,
    setAssetSearch,
    toggleDropdown,
  } = dropdownState;

  const {
    selectedCustomerLabel,
    selectedSiteLabel,
    selectedPlantLabel,
    selectedAssetLabel,
    customerOptions,
    siteOptions,
    plantOptions,
    assetOptions,
    handleCustomerChange,
    clearSiteDependents,
    closeCustomerDropdown,
    closeSiteDropdown,
    closePlantDropdown,
    closeAssetDropdown,
    handleSiteSelect,
    handlePlantSelect,
    handleAssetSelect,
    setSelectedSiteId,
    setSelectedPlantId,
    setSelectedAssetId,
    setSelectedAssetRecordId,
    setAssetDetails,
    setAssets,
  } = selections;

  const {
    selectedCustomerId,
    selectedSiteId,
    selectedPlantId,
    selectedAssetId,
  } = props;

  return (
    <div className="asset-details-filters">
      <div className="filter-dropdowns">
        <AssetSummarySearchableDropdown
          minWidth="220px"
          isOpen={isCustomerOpen}
          search={customerSearch}
          selectedId={selectedCustomerId}
          selectedLabel={selectedCustomerLabel}
          searchPlaceholder="Search customers..."
          emptyPlaceholder="All Customer"
          allOptionLabel="All Customer"
          options={customerOptions}
          onToggle={() => toggleDropdown('customer', isCustomerOpen, setIsCustomerOpen)}
          onSearchChange={setCustomerSearch}
          onSelectAll={() => {
            handleCustomerChange('');
            closeCustomerDropdown();
          }}
          onSelectOption={(customerId) => {
            handleCustomerChange(customerId);
            closeCustomerDropdown();
          }}
        />

        <span className="summary-separator">\</span>

        <AssetSummarySearchableDropdown
          minWidth="200px"
          isOpen={isSiteOpen}
          search={siteSearch}
          selectedId={selectedSiteId}
          selectedLabel={selectedSiteLabel}
          searchPlaceholder="Search sites..."
          emptyPlaceholder="All Sites"
          allOptionLabel="All Sites"
          options={siteOptions}
          onToggle={() => toggleDropdown('site', isSiteOpen, setIsSiteOpen)}
          onSearchChange={setSiteSearch}
          onSelectAll={() => {
            setSelectedSiteId('');
            clearSiteDependents();
            closeSiteDropdown();
          }}
          onSelectOption={handleSiteSelect}
        />

        <span className="summary-separator">\</span>

        <AssetSummarySearchableDropdown
          minWidth="200px"
          isOpen={isPlantOpen}
          search={plantSearch}
          selectedId={selectedPlantId}
          selectedLabel={selectedPlantLabel}
          searchPlaceholder="Search plants..."
          emptyPlaceholder="All Plants"
          allOptionLabel="All Plants"
          options={plantOptions}
          onToggle={() => toggleDropdown('plant', isPlantOpen, setIsPlantOpen)}
          onSearchChange={setPlantSearch}
          onSelectAll={() => {
            setSelectedPlantId('');
            setSelectedAssetId('');
            setSelectedAssetRecordId('');
            setAssetDetails(null);
            setAssets([]);
            closePlantDropdown();
          }}
          onSelectOption={handlePlantSelect}
        />

        <span className="summary-separator">\</span>

        <AssetSummarySearchableDropdown
          minWidth="200px"
          isOpen={isAssetOpen}
          search={assetSearch}
          selectedId={selectedAssetId}
          selectedLabel={selectedAssetLabel}
          searchPlaceholder="Search assets..."
          emptyPlaceholder="Select Asset"
          allOptionLabel="Select Asset"
          options={assetOptions}
          onToggle={() => toggleDropdown('asset', isAssetOpen, setIsAssetOpen)}
          onSearchChange={setAssetSearch}
          onSelectAll={() => {
            setSelectedAssetId('');
            setSelectedAssetRecordId('');
            closeAssetDropdown();
          }}
          onSelectOption={handleAssetSelect}
        />
      </div>
    </div>
  );
};

export default AssetSummaryFilterDropdowns;
